import type { FmpExchangeVariant } from '@/queries';

import type { FinancialOverviewSecurityPrice } from '../../types';

export interface CurrentFinancialOverviewSecurityPrice {
    currency?: string | null;
    exchange?: string | null;
    name?: string | null;
    price?: number;
    symbol: string;
}

const normalize = (value?: string | null) => value?.trim().toUpperCase() ?? '';

const exchangeCode = (variant: FmpExchangeVariant) =>
    variant.exchangeShortName ?? variant.exchange ?? variant.stockExchange ?? undefined;

const priceKey = (exchange: string | null | undefined, symbol: string) => `${normalize(exchange)}:${normalize(symbol)}`;

const finitePrice = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const resolveFinancialOverviewSecurityPrices = (
    variants: FmpExchangeVariant[],
    currentSecurity: CurrentFinancialOverviewSecurityPrice,
): FinancialOverviewSecurityPrice[] => {
    const pricesByKey = new Map<string, FinancialOverviewSecurityPrice>();

    variants.forEach((variant) => {
        if (!finitePrice(variant.price)) {
            return;
        }

        const exchange = exchangeCode(variant);

        pricesByKey.set(priceKey(exchange, variant.symbol), {
            currency: variant.currency,
            current: false,
            exchange,
            name: variant.name,
            price: variant.price,
            stockExchange: variant.stockExchange,
            symbol: variant.symbol,
            timestamp: variant.timestamp,
            type: variant.type,
        });
    });

    const prices = [...pricesByKey.values()];
    const exactCurrentIndex = prices.findIndex(
        (price) =>
            normalize(price.symbol) === normalize(currentSecurity.symbol) &&
            normalize(price.exchange) === normalize(currentSecurity.exchange),
    );
    const symbolCurrentIndex = prices.findIndex(
        (price) => normalize(price.symbol) === normalize(currentSecurity.symbol),
    );
    const currentIndex = exactCurrentIndex >= 0 ? exactCurrentIndex : symbolCurrentIndex;

    if (currentIndex >= 0) {
        prices[currentIndex] = {
            ...prices[currentIndex],
            currency: prices[currentIndex].currency ?? currentSecurity.currency,
            current: true,
            name: prices[currentIndex].name ?? currentSecurity.name,
        };
    } else if (finitePrice(currentSecurity.price)) {
        prices.push({
            currency: currentSecurity.currency,
            current: true,
            exchange: currentSecurity.exchange,
            name: currentSecurity.name,
            price: currentSecurity.price,
            symbol: currentSecurity.symbol,
        });
    }

    return prices.sort((left, right) => {
        if (left.current !== right.current) {
            return left.current ? -1 : 1;
        }

        return `${left.exchange ?? ''}:${left.symbol}`.localeCompare(`${right.exchange ?? ''}:${right.symbol}`);
    });
};
